/**
 * Attendance filters component - Client selector and date range
 */

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/hooks/useTranslation";

interface Klient {
  id: number;
  alias: string;
  meno: string | null;
  priezvisko: string | null;
  odberTrening?: boolean | null;
}

interface AttendanceFiltersProps {
  selectedKlientId: number | null;
  onKlientChange: (klientId: number | null) => void;
  startDate: string;
  onStartDateChange: (date: string) => void;
  endDate: string;
  onEndDateChange: (date: string) => void;
  klienti?: Klient[];
  isSummaryMode: boolean;
  onSummaryModeChange: (enabled: boolean) => void;
  allowAllClients?: boolean;
}

export function AttendanceFilters({
  selectedKlientId,
  onKlientChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  klienti,
  isSummaryMode,
  onSummaryModeChange,
  allowAllClients = true,
}: AttendanceFiltersProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="space-y-2">
        <Label>{t("attendance:filters.filterByClient")}</Label>
        <Select
          value={selectedKlientId?.toString() || (allowAllClients ? "all" : "")}
          onValueChange={value =>
            onKlientChange(value === "all" ? null : Number(value))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder={t("attendance:filters.allClients")} />
          </SelectTrigger>
          <SelectContent>
            {allowAllClients && (
              <SelectItem value="all">
                {t("attendance:filters.allClients")}
              </SelectItem>
            )}
            {klienti
              ?.filter(k => k.odberTrening)
              .map(k => (
                <SelectItem key={k.id} value={k.id.toString()}>
                  {k.alias} - {k.meno} {k.priezvisko}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("attendance:filters.startDate")}</Label>
        <Input
          type="date"
          value={startDate}
          onChange={e => onStartDateChange(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label>{t("attendance:filters.endDate")}</Label>
        <Input
          type="date"
          value={endDate}
          onChange={e => onEndDateChange(e.target.value)}
        />
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="summary-mode"
          checked={isSummaryMode}
          onCheckedChange={onSummaryModeChange}
        />
        <Label htmlFor="summary-mode" className="text-sm font-medium">
          {t("attendance:filters.summaryMode")}
        </Label>
      </div>
    </div>
  );
}
